import React, { useMemo } from 'react'
import BodyText from 'LuxKit/Typography/BodyText'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Group from 'components/utils/Group'
import LineArrowToRightIcon from 'LuxKit/Icons/line/LineArrowToRightIcon'
import LineLeftArrowToLeftIcon from 'LuxKit/Icons/line/LineLeftArrowToLeftIcon'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import LineClipboardNotesIcon from 'LuxKit/Icons/line/LineClipboardNotesIcon'
import LineReceiptIcon from 'LuxKit/Icons/line/LineReceiptIcon'
import LineTagIcon from 'LuxKit/Icons/line/LineTagIcon'
import LineListUlIcon from 'LuxKit/Icons/line/LineListUlIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LineFileContractIcon from 'LuxKit/Icons/line/LineFileContractIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { useIntl } from 'react-intl'
import { camelToTitle } from 'lib/string/stringUtils'
import ThingsToKnowLayout from './ThingsToKnowLayout'
import type { ThingsToKnowRow } from './ThingsToKnowLayout'

interface Props {
  offer: App.BedbankOffer
}

function BedbankThingsToKnow({ offer }: Props) {
  const intl = useIntl()
  const { finePrint, finePrintOverride } = offer.property

  const promotionCancellation = useMemo(() => {
    const policies = offer.promotions
      .map(p => p.cancellationPolicy)
      .filter((p): p is string => !!p)
    return policies.length ? policies.join('\n\n') : undefined
  }, [offer.promotions])

  const promotionFinePrint = useMemo(() => {
    return offer.promotions.find(p => p.finePrint)?.finePrint ?? undefined
  }, [offer.promotions])

  const checkInLabel = intl.formatMessage({
    id: 'RyTQC3NzUG7OireVDUMt',
    defaultMessage: 'Check-in',
    description: 'Form field label for check-in the time row in things to know',
  })
  const checkOutLabel = intl.formatMessage({
    id: 'XeUARGCrmO4SxCe5Xbev',
    defaultMessage: 'Check-out',
    description: 'Form field label for check-out time row in things to know',
  })

  const checkInTime = useMemo(() => {
    const { beginTime, endTime } = finePrint?.checkIn ?? {}
    if (!beginTime && !endTime) return undefined
    if (beginTime && endTime) return `${beginTime} – ${endTime}`
    return beginTime ?? endTime
  }, [finePrint?.checkIn])
  const checkInInstructionsLabel = intl.formatMessage({
    id: 'AGNbSnJcbOJMGXeW2rz2',
    defaultMessage: 'Check-in instructions',
    description: 'Label for check-in instructions row in things to know',
  })
  const instructionsSubLabel = intl.formatMessage({
    id: 'f9tJvUcVVT2pQvcGcl1l',
    defaultMessage: 'Instructions',
    description: 'Sub-heading for check-in instructions in things to know',
  })
  const specialInstructionsSubLabel = intl.formatMessage({
    id: 'BQs4OU5fg5_0hOKW5IV6',
    defaultMessage: 'Special instructions',
    description: 'Sub-heading for special check-in instructions in things to know',
  })
  const feesLabel = intl.formatMessage({
    id: 'UdZNX3zwb5ommTFRPrDh',
    defaultMessage: 'Fees',
    description: 'Form field label for the fees row in things to know',
  })
  const mandatoryFeesLabel = intl.formatMessage({
    id: 'Zp4ZWd6S6x3iKvkQsjL0',
    defaultMessage: 'Mandatory',
    description: 'Sub-heading for mandatory fees in things to know on the offer detail page',
  })
  const optionalFeesLabel = intl.formatMessage({
    id: 'ltUNTm6zT76Nikuaov1v',
    defaultMessage: 'Optional',
    description: 'Sub-heading for optional fees in things to know on the offer detail page',
  })
  const petsLabel = intl.formatMessage({
    id: 'l8rB6xuLpitzNGdmrjVK',
    defaultMessage: 'Pets',
    description: 'Form field label for the pets row in things to know',
  })
  const generalLabel = intl.formatMessage({
    id: 'E_s2ZAJwCD7QO9iVJCad',
    defaultMessage: 'General',
    description: 'Form field label for general info row in things to know',
  })
  const safetyLabel = intl.formatMessage({
    id: '14AEE7L8tS9tHpkS2ZPZ',
    defaultMessage: 'Cleaning and safety',
    description: 'Form field label for safety measures row in things to know',
  })
  const policiesLabel = intl.formatMessage({
    id: 'lAi84gpodyXFAPtn9rMz',
    defaultMessage: 'Policies',
    description: 'Form field label for the policies row in things to know',
  })
  const cancellationLabel = intl.formatMessage({
    id: 'EnAVJQ2V_oME-qJ91Yme',
    defaultMessage: 'Cancellation policy',
    description: 'Label for cancellation policy row in things to know',
  })
  const finePrintLabel = intl.formatMessage({
    id: 'XyfySkseacGwZyjkSAqr',
    defaultMessage: 'Fine print',
    description: 'Form field label for fine print row in things to know',
  })

  const hasCheckInInstructions = !!(finePrint?.checkIn?.instructions || finePrint?.checkIn?.specialInstructions)

  const checkInInstructionsContent = useMemo(() => {
    if (!hasCheckInInstructions) return undefined
    const hasBoth = !!(finePrint?.checkIn?.instructions && finePrint?.checkIn?.specialInstructions)
    return (
      <Group direction="vertical" gap={hasBoth ? 12 : 4}>
        {finePrint?.checkIn?.instructions && (
          <Group direction="vertical" gap={4}>
            {hasBoth && <BodyText variant="medium" weight="bold">{instructionsSubLabel}</BodyText>}
            <BodyText variant="medium" colour="neutral-two">{finePrint.checkIn.instructions}</BodyText>
          </Group>
        )}
        {finePrint?.checkIn?.specialInstructions && (
          <Group direction="vertical" gap={4}>
            {hasBoth && <BodyText variant="medium" weight="bold">{specialInstructionsSubLabel}</BodyText>}
            <BodyText variant="medium" colour="neutral-two">{finePrint.checkIn.specialInstructions}</BodyText>
          </Group>
        )}
      </Group>
    )
  }, [hasCheckInInstructions, finePrint?.checkIn?.specialInstructions, finePrint?.checkIn?.instructions, instructionsSubLabel, specialInstructionsSubLabel])

  const hasFees = !!(finePrint?.fees?.mandatory || finePrint?.fees?.optional)

  const feesContent = useMemo(() => {
    if (!hasFees) return undefined
    return (
      <Group direction="vertical" gap={12}>
        {finePrint?.fees?.mandatory && (
          <Group direction="vertical" gap={4}>
            <BodyText variant="medium" weight="bold">{mandatoryFeesLabel}</BodyText>
            <MarkdownRender type="compact" fontSize="medium" content={finePrint.fees.mandatory} />
          </Group>
        )}
        {finePrint?.fees?.optional && (
          <Group direction="vertical" gap={4}>
            <BodyText variant="medium" weight="bold">{optionalFeesLabel}</BodyText>
            <MarkdownRender type="compact" fontSize="medium" content={finePrint.fees.optional} />
          </Group>
        )}
      </Group>
    )
  }, [hasFees, finePrint?.fees?.mandatory, finePrint?.fees?.optional, mandatoryFeesLabel, optionalFeesLabel])

  const rows = useMemo<Array<ThingsToKnowRow>>(() => {
    const result: Array<ThingsToKnowRow> = []

    if (checkInTime) {
      result.push({
        key: 'check-in',
        icon: <LineArrowToRightIcon size="S" colour="neutral-one" />,
        label: checkInLabel,
        content: checkInTime,
        collapsable: false,
      })
    }

    if (finePrint?.checkOut?.time) {
      result.push({
        key: 'check-out',
        icon: <LineLeftArrowToLeftIcon size="S" colour="neutral-one" />,
        label: checkOutLabel,
        content: finePrint.checkOut.time,
        collapsable: false,
      })
    }

    if (hasCheckInInstructions) {
      result.push({
        key: 'check-in-instructions',
        icon: <LineClipboardNotesIcon size="S" colour="neutral-one" />,
        label: checkInInstructionsLabel,
        content: checkInInstructionsContent!,
        collapsable: true,
      })
    }

    if (hasFees) {
      result.push({
        key: 'fees',
        icon: <LineReceiptIcon size="S" colour="neutral-one" />,
        label: feesLabel,
        content: feesContent!,
        collapsable: true,
      })
    }

    if (finePrint?.pets?.length) {
      result.push({
        key: 'pets',
        icon: <LineTagIcon size="S" colour="neutral-one" />,
        label: petsLabel,
        content: (
          <Group direction="vertical" gap={4}>
            {finePrint.pets.map((item, index) => (
              <BodyText variant="medium" colour="neutral-two" key={`pet-${index}`}>{item}</BodyText>
            ))}
          </Group>
        ),
        collapsable: true,
      })
    }

    if (finePrint?.general?.length) {
      result.push({
        key: 'general',
        icon: <LineListUlIcon size="S" colour="neutral-one" />,
        label: generalLabel,
        content: (
          <Group direction="vertical" gap={4}>
            {finePrint.general.map((item, index) => (
              <BodyText variant="medium" colour="neutral-two" key={`general-${index}`}>{item}</BodyText>
            ))}
          </Group>
        ),
        collapsable: true,
      })
    }

    if (finePrint?.safety?.length) {
      result.push({
        key: 'safety',
        icon: <LineShieldCheckIcon size="S" colour="neutral-one" />,
        label: safetyLabel,
        content: (
          <Group direction="vertical" gap={4}>
            {finePrint.safety.map((item, index) => (
              <BodyText variant="medium" colour="neutral-two" key={`safety-${index}`}>{item}</BodyText>
            ))}
          </Group>
        ),
        collapsable: true,
      })
    }

    if (finePrint?.policies && Object.keys(finePrint.policies).length) {
      const policyEntries = Object.entries(finePrint.policies)
      result.push({
        key: 'policies',
        icon: <LineFileContractIcon size="S" colour="neutral-one" />,
        label: policiesLabel,
        content: (
          <Group direction="vertical" gap={12}>
            {policyEntries.map(([policyKey, policyValue]) => (
              <Group direction="vertical" gap={4} key={policyKey}>
                <BodyText variant="medium" weight="bold">{camelToTitle(policyKey)}</BodyText>
                <MarkdownRender type="compact" fontSize="medium" content={policyValue} />
              </Group>
            ))}
          </Group>
        ),
        collapsable: true,
      })
    }

    if (promotionCancellation) {
      result.push({
        key: 'cancellation',
        icon: <LineRefundIcon size="S" colour="neutral-one" />,
        label: cancellationLabel,
        content: <MarkdownRender type="compact" fontSize="medium" content={promotionCancellation} />,
        collapsable: true,
      })
    }

    const remainingContent = finePrintOverride ?? promotionFinePrint
    if (remainingContent) {
      result.push({
        key: 'fine-print',
        icon: <LineInfoCircleIcon size="S" colour="neutral-one" />,
        label: finePrintLabel,
        content: <MarkdownRender type="compact" fontSize="medium" content={remainingContent} />,
        collapsable: true,
      })
    }

    return result
  }, [
    checkInTime, checkInLabel,
    finePrint, checkOutLabel,
    hasCheckInInstructions, checkInInstructionsLabel, checkInInstructionsContent,
    hasFees, feesLabel, feesContent,
    petsLabel, generalLabel, safetyLabel, policiesLabel,
    promotionCancellation, cancellationLabel,
    finePrintOverride, promotionFinePrint, finePrintLabel,
  ])

  return <ThingsToKnowLayout rows={rows} />
}

export default BedbankThingsToKnow
